import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  GraduationCap, User, Mail, Phone, MapPin, BookOpen, 
  Clock, FileText, Upload, CheckCircle, ArrowRight, ArrowLeft,
  Globe, Shield, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// UK and Sri Lanka subjects
const SUBJECTS_BY_COUNTRY = {
  UK: [
    'Mathematics', 'English', 'Science', 'Physics', 'Chemistry', 'Biology',
    'History', 'Geography', 'French', 'Spanish', 'German', 'Computer Science',
    'Economics', 'Business Studies', 'Psychology', 'Music', 'Art', 'Drama',
    'Further Maths', 'Statistics'
  ],
  LK: [
    'Mathematics', 'English', 'Science', 'Sinhala', 'Tamil', 'Physics',
    'Chemistry', 'Biology', 'Combined Maths', 'Commerce', 'Accounting',
    'Economics', 'Business Studies', 'ICT', 'Buddhism', 'History',
    'Geography', 'Political Science', 'Logic'
  ]
};

const LEVELS = {
  UK: ['Primary', 'KS2', 'KS3', 'GCSE', 'A-Level', 'IGCSE', '11+', '13+', 'University'],
  LK: ['Grade 1-5', 'Grade 6-9', 'O/L', 'A/L', 'Grade 5 Scholarship', 'University']
};

const UK_REGIONS = [
  { value: 'london', label: 'London', cities: ['Central London', 'North London', 'South London', 'East London', 'West London'] },
  { value: 'south_east', label: 'South East', cities: ['Brighton', 'Oxford', 'Reading', 'Southampton', 'Canterbury'] },
  { value: 'south_west', label: 'South West', cities: ['Bristol', 'Bath', 'Plymouth', 'Exeter'] },
  { value: 'midlands', label: 'Midlands', cities: ['Birmingham', 'Leicester', 'Nottingham', 'Derby', 'Coventry'] },
  { value: 'north_west', label: 'North West', cities: ['Manchester', 'Liverpool', 'Preston', 'Chester'] },
  { value: 'north_east', label: 'North East', cities: ['Newcastle', 'Durham', 'Sunderland', 'Middlesbrough'] },
  { value: 'yorkshire', label: 'Yorkshire', cities: ['Leeds', 'Sheffield', 'Bradford', 'York', 'Hull'] },
  { value: 'scotland', label: 'Scotland', cities: ['Edinburgh', 'Glasgow', 'Aberdeen', 'Dundee'] },
  { value: 'wales', label: 'Wales', cities: ['Cardiff', 'Swansea', 'Newport', 'Wrexham'] },
  { value: 'online_only', label: 'Online Only', cities: [] },
];

const LK_REGIONS = [
  { value: 'western', label: 'Western Province', cities: ['Colombo', 'Gampaha', 'Kalutara', 'Negombo', 'Moratuwa'] },
  { value: 'central', label: 'Central Province', cities: ['Kandy', 'Matale', 'Nuwara Eliya'] },
  { value: 'southern', label: 'Southern Province', cities: ['Galle', 'Matara', 'Hambantota'] },
  { value: 'northern', label: 'Northern Province', cities: ['Jaffna', 'Kilinochchi', 'Mannar', 'Vavuniya'] },
  { value: 'eastern', label: 'Eastern Province', cities: ['Trincomalee', 'Batticaloa', 'Ampara'] },
  { value: 'north_western', label: 'North Western Province', cities: ['Kurunegala', 'Puttalam', 'Chilaw'] },
  { value: 'north_central', label: 'North Central Province', cities: ['Anuradhapura', 'Polonnaruwa'] },
  { value: 'uva', label: 'Uva Province', cities: ['Badulla', 'Monaragala'] },
  { value: 'sabaragamuwa', label: 'Sabaragamuwa Province', cities: ['Ratnapura', 'Kegalle'] },
  { value: 'online_only', label: 'Online Only', cities: [] },
];

const VERIFICATION_TYPES = {
  UK: [
    { key: 'dbs_basic', name: 'DBS Basic Check', required: false },
    { key: 'dbs_enhanced', name: 'DBS Enhanced Check', required: true },
    { key: 'qts', name: 'Qualified Teacher Status (QTS)', required: false },
    { key: 'pgce', name: 'PGCE Certificate', required: false },
    { key: 'degree_verified', name: 'Degree Certificate', required: false },
  ],
  LK: [
    { key: 'police_clearance_lk', name: 'Police Clearance Certificate', required: true },
    { key: 'slts_registered', name: 'SLTS Registration', required: false },
    { key: 'moe_certified', name: 'MOE Teacher Certificate', required: false },
    { key: 'ncoe_diploma', name: 'NCOE Diploma', required: false },
    { key: 'degree_verified', name: 'Degree Certificate', required: false },
  ]
};

export default function TutorRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: '',
    email: '',
    phone: '',
    country: 'UK',
    region: '',
    city: '',
    
    // Step 2: Teaching Details
    subjects: [],
    levels: [],
    hourly_rate: '',
    experience_years: '',
    languages: ['English'],
    online_available: true,
    in_person_available: false,
    
    // Step 3: Profile
    bio: '',
    teaching_style: '',
    qualifications: [],
    newQualification: '',
    
    // Step 4: Documents
    documents: [],
  });
  
  const regions = formData.country === 'UK' ? UK_REGIONS : LK_REGIONS;
  const currentRegion = regions.find(r => r.value === formData.region);
  const cities = currentRegion?.cities || [];
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const toggleArrayItem = (field, item) => {
    setFormData(prev => {
      const arr = prev[field];
      if (arr.includes(item)) {
        return { ...prev, [field]: arr.filter(i => i !== item) };
      } else {
        return { ...prev, [field]: [...arr, item] };
      }
    });
  };
  
  const addQualification = () => {
    if (formData.newQualification.trim()) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, prev.newQualification.trim()],
        newQualification: ''
      }));
    }
  };
  
  const removeQualification = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };
  
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP image or PDF document');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploadingDoc(true);
    
    try {
      // For now, store locally - will upload after registration
      const reader = new FileReader();
      reader.onload = (event) => {
        const docData = {
          type: docType,
          name: VERIFICATION_TYPES[formData.country].find(v => v.key === docType)?.name || docType,
          fileName: file.name,
          fileSize: file.size,
          file: file, // Store file reference for later upload
          preview: file.type.startsWith('image/') ? event.target.result : null,
          status: 'pending_upload'
        };
        
        setFormData(prev => ({
          ...prev,
          documents: [...prev.documents.filter(d => d.type !== docType), docData]
        }));
        
        toast.success(`${docData.name} added`);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process document');
    } finally {
      setUploadingDoc(false);
    }
  };
  
  const removeDocument = (docType) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(d => d.type !== docType)
    }));
  };
  
  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.name.trim()) { toast.error('Please enter your name'); return false; }
        if (!formData.email.trim()) { toast.error('Please enter your email'); return false; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { toast.error('Please enter a valid email'); return false; }
        if (!formData.region) { toast.error('Please select a region'); return false; }
        return true;
      case 2:
        if (formData.subjects.length === 0) { toast.error('Please select at least one subject'); return false; }
        if (formData.levels.length === 0) { toast.error('Please select at least one level'); return false; }
        if (!formData.hourly_rate || parseFloat(formData.hourly_rate) <= 0) { toast.error('Please enter a valid hourly rate'); return false; }
        return true;
      case 3:
        if (!formData.bio.trim() || formData.bio.length < 50) { toast.error('Please write a bio (at least 50 characters)'); return false; }
        return true;
      case 4:
        // Documents are optional but encouraged
        return true;
      default:
        return true;
    }
  };
  
  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 4));
    }
  };
  
  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };
  
  const submitRegistration = async () => {
    setLoading(true);
    
    try {
      // Step 1: Register tutor
      const registrationData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        subjects: formData.subjects,
        levels: formData.levels,
        hourly_rate: parseFloat(formData.hourly_rate),
        bio: formData.bio,
        teaching_style: formData.teaching_style,
        qualifications: formData.qualifications,
        experience_years: parseInt(formData.experience_years) || 0,
        languages: formData.languages,
        online_available: formData.online_available,
        in_person_available: formData.in_person_available,
      };
      
      const response = await fetch(`${API_URL}/api/education-tuition/tutors/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      const tutorId = data.tutor_id;
      
      // Step 2: Upload documents
      for (const doc of formData.documents) {
        if (doc.file) {
          const formDataUpload = new FormData();
          formDataUpload.append('file', doc.file);
          formDataUpload.append('verification_type', doc.type);
          
          try {
            await fetch(`${API_URL}/api/education-tuition/tutors/${tutorId}/upload-document`, {
              method: 'POST',
              body: formDataUpload,
            });
          } catch (uploadError) {
            console.error('Document upload failed:', uploadError);
          }
        }
      }
      
      toast.success('Registration successful! Please complete verification to start accepting bookings.');
      
      // Navigate to verification page
      navigate(`/tutor/${tutorId}/verification`);
      
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Become a Tutor</h1>
          <p className="text-gray-600 mt-2">Join our network of trusted educators</p>
        </div>
        
        {/* Progress indicator */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((num) => (
            <React.Fragment key={num}>
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${
                  step >= num 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {step > num ? <CheckCircle className="w-5 h-5" /> : num}
              </div>
              {num < 4 && (
                <div className={`w-16 h-1 mx-1 transition-all ${
                  step > num ? 'bg-indigo-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {step === 1 && <><User className="w-5 h-5 text-indigo-600" /> Basic Information</>}
              {step === 2 && <><BookOpen className="w-5 h-5 text-indigo-600" /> Teaching Details</>}
              {step === 3 && <><FileText className="w-5 h-5 text-indigo-600" /> Your Profile</>}
              {step === 4 && <><Shield className="w-5 h-5 text-indigo-600" /> Verification Documents</>}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Tell us about yourself'}
              {step === 2 && 'What and how do you teach?'}
              {step === 3 && 'Help students get to know you'}
              {step === 4 && 'Upload verification documents for trust & safety'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input 
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="Your full name"
                      data-testid="tutor-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input 
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="your.email@example.com"
                      data-testid="tutor-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input 
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder={formData.country === 'UK' ? '+44 7XXX XXXXXX' : '+94 7X XXXXXXX'}
                      data-testid="tutor-phone-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Country *</Label>
                    <Select 
                      value={formData.country} 
                      onValueChange={(v) => {
                        updateField('country', v);
                        updateField('region', '');
                        updateField('city', '');
                        updateField('subjects', []);
                        updateField('levels', []);
                      }}
                    >
                      <SelectTrigger data-testid="country-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                        <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Region *</Label>
                    <Select 
                      value={formData.region} 
                      onValueChange={(v) => {
                        updateField('region', v);
                        updateField('city', '');
                      }}
                    >
                      <SelectTrigger data-testid="region-select">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map(region => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {cities.length > 0 && (
                    <div>
                      <Label>City</Label>
                      <Select value={formData.city} onValueChange={(v) => updateField('city', v)}>
                        <SelectTrigger data-testid="city-select">
                          <SelectValue placeholder="Select city (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {cities.map(city => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Step 2: Teaching Details */}
            {step === 2 && (
              <>
                <div className="space-y-4">
                  <div>
                    <Label>Subjects You Teach *</Label>
                    <p className="text-sm text-gray-500 mb-2">Select all that apply</p>
                    <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                      {SUBJECTS_BY_COUNTRY[formData.country].map(subject => (
                        <Badge 
                          key={subject}
                          variant={formData.subjects.includes(subject) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleArrayItem('subjects', subject)}
                          data-testid={`subject-${subject.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {subject}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Levels You Teach *</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {LEVELS[formData.country].map(level => (
                        <Badge 
                          key={level}
                          variant={formData.levels.includes(level) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleArrayItem('levels', level)}
                          data-testid={`level-${level.toLowerCase().replace(/[\s/+]+/g, '-')}`}
                        >
                          {level}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hourly_rate">Hourly Rate ({formData.country === 'UK' ? '£' : 'Rs.'}) *</Label>
                      <Input 
                        id="hourly_rate"
                        type="number"
                        value={formData.hourly_rate}
                        onChange={(e) => updateField('hourly_rate', e.target.value)}
                        placeholder={formData.country === 'UK' ? '25' : '1500'}
                        min="0"
                        data-testid="hourly-rate-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="experience_years">Years of Experience</Label>
                      <Input 
                        id="experience_years"
                        type="number"
                        value={formData.experience_years}
                        onChange={(e) => updateField('experience_years', e.target.value)}
                        placeholder="0"
                        min="0"
                        data-testid="experience-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Teaching Format</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox 
                          checked={formData.online_available}
                          onCheckedChange={(v) => updateField('online_available', v)}
                          data-testid="online-checkbox"
                        />
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" /> Online
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox 
                          checked={formData.in_person_available}
                          onCheckedChange={(v) => updateField('in_person_available', v)}
                          data-testid="in-person-checkbox"
                        />
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" /> In-Person
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Step 3: Profile */}
            {step === 3 && (
              <>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="bio">About You *</Label>
                    <p className="text-sm text-gray-500 mb-2">Tell students about your background and teaching experience</p>
                    <Textarea 
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => updateField('bio', e.target.value)}
                      placeholder="I am a passionate educator with experience in..."
                      rows={5}
                      data-testid="bio-textarea"
                    />
                    <p className="text-xs text-gray-400 mt-1">{formData.bio.length}/500 characters (min 50)</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="teaching_style">Teaching Style</Label>
                    <Textarea 
                      id="teaching_style"
                      value={formData.teaching_style}
                      onChange={(e) => updateField('teaching_style', e.target.value)}
                      placeholder="My teaching approach focuses on..."
                      rows={3}
                      data-testid="teaching-style-textarea"
                    />
                  </div>
                  
                  <div>
                    <Label>Qualifications</Label>
                    <div className="flex gap-2 mt-2">
                      <Input 
                        value={formData.newQualification}
                        onChange={(e) => updateField('newQualification', e.target.value)}
                        placeholder="e.g., BSc Mathematics, PGCE"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQualification())}
                        data-testid="qualification-input"
                      />
                      <Button type="button" onClick={addQualification} variant="outline">Add</Button>
                    </div>
                    {formData.qualifications.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.qualifications.map((qual, idx) => (
                          <Badge key={idx} variant="secondary" className="pr-1">
                            {qual}
                            <button 
                              onClick={() => removeQualification(idx)}
                              className="ml-1 hover:text-red-500"
                            >×</button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            
            {/* Step 4: Verification Documents */}
            {step === 4 && (
              <>
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-900">Trust & Safety Verification</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          {formData.country === 'UK' 
                            ? 'DBS Enhanced Check is required for tutoring children. Other documents help build trust with parents.'
                            : 'Police Clearance is required for tutoring. SLTS registration and MOE certification help build trust with parents.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {VERIFICATION_TYPES[formData.country].map(docType => {
                      const uploadedDoc = formData.documents.find(d => d.type === docType.key);
                      
                      return (
                        <div 
                          key={docType.key}
                          className={`border rounded-lg p-4 ${uploadedDoc ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {uploadedDoc ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <Upload className="w-5 h-5 text-gray-400" />
                              )}
                              <div>
                                <p className="font-medium">
                                  {docType.name}
                                  {docType.required && <span className="text-red-500 ml-1">*</span>}
                                </p>
                                {uploadedDoc && (
                                  <p className="text-sm text-green-600">{uploadedDoc.fileName}</p>
                                )}
                              </div>
                            </div>
                            
                            <div>
                              {uploadedDoc ? (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => removeDocument(docType.key)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  Remove
                                </Button>
                              ) : (
                                <label className="cursor-pointer">
                                  <input 
                                    type="file"
                                    className="hidden"
                                    accept="image/jpeg,image/png,image/webp,application/pdf"
                                    onChange={(e) => handleDocumentUpload(e, docType.key)}
                                    data-testid={`upload-${docType.key}`}
                                  />
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    asChild
                                    disabled={uploadingDoc}
                                  >
                                    <span>
                                      <Upload className="w-4 h-4 mr-1" />
                                      Upload
                                    </span>
                                  </Button>
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-indigo-900">AI-Powered Verification</h4>
                        <p className="text-sm text-indigo-700 mt-1">
                          Our AI system will review your documents for faster approval. Most verifications are completed within 24 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t">
              {step > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 4 ? (
                <Button onClick={nextStep} data-testid="next-step-btn">
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={submitRegistration} 
                  disabled={loading}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="submit-registration-btn"
                >
                  {loading ? 'Registering...' : 'Complete Registration'}
                  <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
